'use client';

import { Box, freddyTheme, Modal, ModalBody, Tag, Typography } from '@coolblue-development/freddy';
import selectContentTriggerPromoIcons from '@coolblue-development/ga4-events/triggers/PromoIcons/selectContentTrigger';
import showPromoIconTrigger from '@coolblue-development/ga4-events/triggers/PromoIcons/showPromoIconTrigger';
import readMoreTrigger from '@coolblue-development/ga4-events/triggers/PromoLabels/readMoreTrigger';
import selectContentTriggerPromoLabels from '@coolblue-development/ga4-events/triggers/PromoLabels/selectContentTrigger';
import showPromoLabelTrigger from '@coolblue-development/ga4-events/triggers/PromoLabels/showPromoLabelTrigger';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { Image } from '@coolblue-development/next-image/client';
import { ImageProps } from '@coolblue-development/next-image/types';
import { PromoIcon } from '@features/product/typings';
import { useIsLeasePage } from '@hooks/useIsLeasePage';
import { useTranslation } from '@i18n/useTranslation';
import { decodedHtml } from '@utils/decodedHtml';
import { Fragment, ReactElement, useEffect, useRef, useState } from 'react';

export default function PromoIcons({
  promoIcon,
  productName
}: {
  promoIcon: PromoIcon;
  productName: string;
}): ReactElement[] {
  const isLeasePage = useIsLeasePage();
  const { trackEvent } = useAnalytics();
  const [promoIconModalIsOpen, setPromoIconModalIsOpen] = useState(false);
  const promoIcons: ReactElement[] = [];
  const [title, setTitle] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation('webshop');
  const modalTranslations = useFreddyTranslations('modal');

  useEffect(() => {
    if (promoIcon?.displayText) {
      setTitle(decodedHtml(promoIcon.displayText));
    }
  }, [promoIcon]);

  useEffect(() => {
    if (!promoIconModalIsOpen || !ref.current) {
      return;
    }

    const modalBody = ref.current;
    const links = modalBody?.querySelectorAll('a');

    if (!links) {
      return;
    }

    const handleClick = () => {
      trackEvent(
        readMoreTrigger({
          subject_type: promoIcon?.displayText ? 'label' : 'item',
          subject_id: promoIcon?.displayText ? promoIcon?.displayText : promoIcon?.image?.id || ''
        })
      );
    };

    links.forEach(link => link.addEventListener('click', handleClick));

    return () => {
      links.forEach(link => link.removeEventListener('click', handleClick));
    };
  }, [promoIcon?.displayText, promoIcon?.image?.id, promoIconModalIsOpen, trackEvent]);

  const displayText = promoIcon?.displayText ?? '';

  const handlePromoIconClick = () => {
    const content = promoIcon?.displayText ?? promoIcon?.image?.id;
    if (!content) return;

    setPromoIconModalIsOpen(true);

    const shouldTrackLabel = Boolean(promoIcon?.displayText && promoIcon?.explanation);
    const shouldTrackIcon = Boolean(promoIcon?.image?.id && promoIcon?.explanation);

    if (shouldTrackLabel) {
      trackEvent(selectContentTriggerPromoLabels({ content }));
    }

    if (shouldTrackIcon) {
      trackEvent(selectContentTriggerPromoIcons({ content }));
    }
  };

  if (promoIcon?.displayText) {
    promoIcons.push(
      <Fragment key={promoIcon.displayText}>
        <TrackImmediate trigger={showPromoLabelTrigger()} />
        <Tag
          dangerouslySetInnerHTML={{ __html: displayText }}
          onClick={handlePromoIconClick}
          size='large'
          variant='subtle'
          component={promoIcon.displayText && promoIcon.explanation ? 'button' : 'span'}
        />
        {promoIcon.explanation && promoIcon.displayText && (
          <Modal
            open={promoIconModalIsOpen}
            onClose={() => setPromoIconModalIsOpen(false)}
            size='medium'
            title={title}
            translations={modalTranslations}
            body={
              <ModalBody>
                <Typography dangerouslySetInnerHTML={{ __html: promoIcon.explanation }} ref={ref} />
              </ModalBody>
            }
          />
        )}
      </Fragment>
    );
  }

  if (promoIcon?.image?.id && promoIcon?.image?.collectionName) {
    promoIcons.push(
      <Fragment key={promoIcon.image.id}>
        <TrackImmediate trigger={showPromoIconTrigger()} />
        <Box
          sx={{
            '& img': {
              maxWidth: '8.8rem',
              maxHeight: '10rem',
              [freddyTheme.breakpoint.minWidth('sm')]: {
                maxHeight: '12rem'
              }
            }
          }}
          {...(promoIcon.displayText === null &&
            promoIcon.explanation && {
              onClick: handlePromoIconClick,
              sx: {
                '&:hover': {
                  cursor: 'pointer !important'
                }
              }
            })}
        >
          <Image
            imageId={promoIcon.image.id}
            fileCollection={promoIcon.image.collectionName as ImageProps['fileCollection']}
            width={88}
            height={80}
            fullHeight
            alt={t('mediaGallery.promotionalLabelForProduct', { productName })}
            loading='eager'
            background='transparent'
          />
        </Box>
        {promoIcon.displayText === null && promoIcon.explanation && (
          <Modal
            open={promoIconModalIsOpen}
            onClose={() => setPromoIconModalIsOpen(false)}
            size='medium'
            translations={modalTranslations}
            body={
              <ModalBody>
                <Typography
                  pt={500}
                  dangerouslySetInnerHTML={{ __html: promoIcon.explanation }}
                  ref={ref}
                />
              </ModalBody>
            }
          />
        )}
      </Fragment>
    );
  }

  if (isLeasePage) {
    return [];
  }

  return promoIcons;
}
